import { ReactNode } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

/**
 * Modal dialog.
 *
 * Radix handles what a hand-rolled overlay usually gets wrong: focus moves into
 * the dialog and returns to whatever opened it, Tab is trapped inside, the page
 * behind stops scrolling, Escape and outside clicks dismiss, and screen readers
 * are told what the thing is. The styling is the editor's own.
 */
export function Modal({
  title,
  onClose,
  children,
  footer,
  width,
  description,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
  description?: string;
}): JSX.Element {
  return (
    <RadixDialog.Root open onOpenChange={(open) => !open && onClose()}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="modal-backdrop" />
        <RadixDialog.Content className="modal modal-centred" style={width ? { width } : undefined}>
          <header className="modal-header">
            <RadixDialog.Title asChild>
              <span>{title}</span>
            </RadixDialog.Title>
            <span className="spacer" />
            <RadixDialog.Close asChild>
              <button className="btn icon" aria-label="Close">
                <X size={15} />
              </button>
            </RadixDialog.Close>
          </header>
          {description ? (
            <RadixDialog.Description className="visually-hidden">{description}</RadixDialog.Description>
          ) : (
            /* Radix warns when a dialog has no description; the title carries
               the meaning for these, so it is explicitly opted out of. */
            <RadixDialog.Description className="visually-hidden">{title}</RadixDialog.Description>
          )}
          <div className="modal-body">{children}</div>
          {footer ? <footer className="modal-footer">{footer}</footer> : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
